import type { ConfigKey, ConfigKind, ConfigValue } from '@/domain';
import { configKeyToString } from '@/domain';
import { mockStore } from '@/lib/mock-store';
import { getSupabase, isMockBackend } from '@/lib/supabase';

interface SetInput {
  key: ConfigKey;
  value: number;
  effective_from?: string;
  note?: string;
  actor: string;
}

function randomId(): string {
  return `cfg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export const configService = {
  async listCurrent(kind?: ConfigKind): Promise<ConfigValue[]> {
    if (isMockBackend) {
      const all = mockStore.getAll('config_values').filter((v) => v.is_current);
      return kind ? all.filter((v) => v.kind === kind) : all;
    }
    const supabase = getSupabase()!;
    let query = supabase.from('config_values').select('*').eq('is_current', true);
    if (kind) query = query.eq('kind', kind);
    const { data, error } = await query.order('key', { ascending: true });
    if (error) throw error;
    return (data ?? []) as ConfigValue[];
  },

  async listAll(): Promise<ConfigValue[]> {
    if (isMockBackend) return mockStore.getAll('config_values');
    const supabase = getSupabase()!;
    const { data, error } = await supabase
      .from('config_values')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ConfigValue[];
  },

  async history(key: string): Promise<ConfigValue[]> {
    if (isMockBackend) {
      return mockStore
        .getAll('config_values')
        .filter((v) => v.key === key)
        .sort((a, b) => b.version - a.version);
    }
    const supabase = getSupabase()!;
    const { data, error } = await supabase
      .from('config_values')
      .select('*')
      .eq('key', key)
      .order('version', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ConfigValue[];
  },

  async getCurrent(key: ConfigKey | string): Promise<ConfigValue | null> {
    const keyStr = typeof key === 'string' ? key : configKeyToString(key);
    if (isMockBackend) {
      return (
        mockStore
          .getAll('config_values')
          .find((v) => v.key === keyStr && v.is_current) ?? null
      );
    }
    const supabase = getSupabase()!;
    const { data, error } = await supabase
      .from('config_values')
      .select('*')
      .eq('key', keyStr)
      .eq('is_current', true)
      .maybeSingle();
    if (error) throw error;
    return (data as ConfigValue | null) ?? null;
  },

  /**
   * Create a new version of a config key. Demotes prior current (mock backend
   * replicates the SQL trigger).
   */
  async setValue(input: SetInput): Promise<ConfigValue> {
    const keyStr = configKeyToString(input.key);
    const prior = await this.getCurrent(keyStr);
    const nextVersion = (prior?.version ?? 0) + 1;
    const row: ConfigValue = {
      id: randomId(),
      key: keyStr,
      kind: input.key.kind,
      value: input.value,
      version: nextVersion,
      is_current: true,
      effective_from: input.effective_from ?? todayISO(),
      note: input.note ?? '',
      created_at: new Date().toISOString(),
      created_by: input.actor,
    };

    if (isMockBackend) {
      // Demote prior
      mockStore.update(
        'config_values',
        (r) => r.key === keyStr && r.is_current,
        { is_current: false },
      );
      mockStore.append('config_values', row);
      return row;
    }
    const supabase = getSupabase()!;
    const { data, error } = await supabase
      .from('config_values')
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return data as ConfigValue;
  },
};
